import { StyleSheet } from 'react-native';

export const explorerColors = {
  background: '#ffffff',
  surface: '#f8fafc',
  border: '#e2e8f0',
  text: '#0f172a',
  muted: '#64748b',
  subtle: '#94a3b8',
  accent: '#2563eb',
  accentSoft: '#dbeafe',
  error: '#dc2626',
  success: '#15803d',
  overlay: 'rgba(0, 0, 0, 0.45)',
};

export const explorerStyles = StyleSheet.create({
  fullScreen: {
    flex: 1,
    backgroundColor: explorerColors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: explorerColors.border,
    backgroundColor: explorerColors.background,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: explorerColors.text,
  },
  headerAction: {
    fontSize: 16,
    color: explorerColors.accent,
    fontWeight: '600',
  },
  tabRow: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: explorerColors.border,
    backgroundColor: explorerColors.surface,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: explorerColors.accent,
  },
  tabLabel: {
    fontSize: 14,
    color: explorerColors.muted,
    fontWeight: '500',
  },
  tabLabelActive: {
    color: explorerColors.accent,
    fontWeight: '700',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: explorerColors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  infoRow: {
    marginBottom: 10,
  },
  infoLabel: {
    fontSize: 12,
    color: explorerColors.subtle,
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 14,
    color: explorerColors.text,
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: explorerColors.surface,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: explorerColors.border,
  },
  listItemTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: explorerColors.text,
    fontFamily: 'monospace',
  },
  listItemMeta: {
    fontSize: 12,
    color: explorerColors.muted,
  },
  searchInput: {
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: explorerColors.text,
    backgroundColor: explorerColors.background,
    marginBottom: 12,
  },
  primaryButton: {
    backgroundColor: explorerColors.accent,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  primaryButtonDisabled: {
    opacity: 0.5,
  },
  primaryButtonLabel: {
    color: '#f8fafc',
    fontSize: 15,
    fontWeight: '600',
  },
  secondaryButton: {
    backgroundColor: '#1e293b',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  secondaryButtonLabel: {
    color: '#f8fafc',
    fontSize: 15,
    fontWeight: '600',
  },
  ghostButton: {
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    backgroundColor: explorerColors.background,
  },
  ghostButtonLabel: {
    color: explorerColors.text,
    fontSize: 15,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 13,
    color: explorerColors.error,
    marginTop: 8,
  },
  hintText: {
    fontSize: 12,
    color: explorerColors.muted,
    marginTop: 8,
    lineHeight: 18,
  },
  placeholder: {
    fontSize: 14,
    color: explorerColors.subtle,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 24,
  },
  sqlInput: {
    minHeight: 120,
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    fontFamily: 'monospace',
    color: explorerColors.text,
    textAlignVertical: 'top',
    backgroundColor: explorerColors.surface,
  },
  sqlTabRoot: {
    flex: 1,
    minHeight: 0,
  },
  sqlQueryPane: {
    flexShrink: 0,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: explorerColors.border,
    backgroundColor: explorerColors.background,
  },
  sqlInputCompact: {
    minHeight: 96,
    maxHeight: 120,
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    fontFamily: 'monospace',
    color: explorerColors.text,
    textAlignVertical: 'top',
    backgroundColor: explorerColors.surface,
  },
  sqlResultsPane: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  sqlResultsEmpty: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  subTabRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  subTab: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: explorerColors.border,
    backgroundColor: explorerColors.background,
  },
  subTabActive: {
    borderColor: explorerColors.accent,
    backgroundColor: explorerColors.accentSoft,
  },
  subTabLabel: {
    fontSize: 13,
    color: explorerColors.muted,
  },
  subTabLabelActive: {
    color: explorerColors.accent,
    fontWeight: '600',
  },
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: explorerColors.border,
  },
  paginationLabel: {
    fontSize: 12,
    color: explorerColors.muted,
  },
});

export const tableStyles = StyleSheet.create({
  viewRoot: {
    flex: 1,
    minHeight: 0,
  },
  viewToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  viewMeta: {
    flex: 1,
    fontSize: 12,
    color: explorerColors.muted,
  },
  viewToggle: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  viewToggleButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: explorerColors.background,
  },
  viewToggleButtonActive: {
    backgroundColor: explorerColors.accentSoft,
  },
  viewToggleLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: explorerColors.muted,
  },
  viewToggleLabelActive: {
    color: explorerColors.accent,
  },
  footerHint: {
    flexShrink: 0,
    marginTop: 8,
    fontSize: 11,
    color: explorerColors.subtle,
    textAlign: 'center',
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: explorerColors.background,
  },
  horizontalScroll: {
    flex: 1,
  },
  scrollHint: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 11,
    color: explorerColors.muted,
    backgroundColor: explorerColors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: explorerColors.border,
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderBottomWidth: 1,
    borderBottomColor: explorerColors.border,
  },
  headerCell: {
    backgroundColor: '#f1f5f9',
  },
  headerText: {
    fontSize: 12,
    fontWeight: '700',
    color: explorerColors.text,
    fontFamily: 'monospace',
  },
  bodyList: {
    flex: 1,
  },
  dataRow: {
    flexDirection: 'row',
    backgroundColor: explorerColors.background,
  },
  dataRowAlt: {
    backgroundColor: explorerColors.surface,
  },
  cell: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: explorerColors.border,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 13,
    color: explorerColors.text,
  },
  cellTextMono: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  cellNull: {
    color: explorerColors.subtle,
    fontStyle: 'italic',
  },
  copiedHint: {
    marginTop: 2,
    fontSize: 10,
    color: explorerColors.accent,
    fontWeight: '600',
  },
  cardList: {
    paddingBottom: 8,
    gap: 8,
  },
  card: {
    borderWidth: 1,
    borderColor: explorerColors.border,
    borderRadius: 8,
    backgroundColor: explorerColors.surface,
    padding: 12,
    gap: 10,
  },
  cardField: {
    gap: 2,
  },
  cardLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: explorerColors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cardValue: {
    fontSize: 14,
    color: explorerColors.text,
  },
  cardValueMono: {
    fontFamily: 'monospace',
    fontSize: 13,
  },
  detailBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 16,
  },
  detailDataPane: {
    flex: 1,
    minHeight: 0,
  },
});
